import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { describeApiError, type AuthField } from '@/lib/auth-errors'
import { useAuthStore, type LoginMethod } from '@/stores/auth-store'
import { Button, Notice, TextField } from '@/components/ui/primitives'

/**
 * 登录面板。
 *
 * AUTH 九个码在这里各有各的界面动作(契约 §3.3)：
 *   - `CODE_INVALID` / `PASSWORD_INVALID` → 对应输入框标红；
 *   - `CODE_TOO_FREQUENT` → 倒计时禁用按钮，⚠ **不自动重试**(它 retryable=false，
 *     重试只会继续撞冷却，而它和 COMMON.RATE_LIMITED 都是 429)；
 *   - `CODE_EXPIRED` → 提示重新获取；
 *   - `ACCOUNT_DISABLED` → 终态，不给重试；
 *   - 其余 → 整体提示 + 显示 traceId。
 */
export function LoginSheet() {
  const { t } = useTranslation()
  const pending = useAuthStore((s) => s.pending)
  const login = useAuthStore((s) => s.login)
  const sendCode = useAuthStore((s) => s.sendCode)
  const cancelLogin = useAuthStore((s) => s.cancelLogin)

  const [method, setMethod] = useState<LoginMethod>('sms')
  const [phone, setPhone] = useState('')
  const [smsCode, setSmsCode] = useState('')
  const [password, setPassword] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const [fieldError, setFieldError] = useState<{ field: AuthField; message: string } | null>(null)
  const [notice, setNotice] = useState<{
    tone: 'info' | 'error' | 'success'
    message: string
    traceId?: string
  } | null>(null)
  const [busy, setBusy] = useState(false)

  // 冷却倒计时。⚠ 用「剩余秒数」而不是「到期时刻」，因为服务端给的就是剩余秒数
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((n) => n - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  function applyError(error: unknown): void {
    const action = describeApiError(error)
    setFieldError(null)
    switch (action.kind) {
      case 'field':
        setFieldError({ field: action.field, message: t(action.messageKey) })
        setNotice(null)
        return
      case 'countdown':
        // 服务端没告诉我们还剩多久，给一个保守的整段冷却
        setCooldown(60)
        setNotice({ tone: 'error', message: t(action.messageKey) })
        return
      case 'refetch-code':
        setCooldown(0)
        setNotice({ tone: 'error', message: t(action.messageKey) })
        return
      case 'terminal':
        setNotice({ tone: 'error', message: t(action.messageKey) })
        return
      default:
        setNotice({ tone: 'error', message: t(action.messageKey), traceId: action.traceId })
    }
  }

  async function onSendCode(): Promise<void> {
    setBusy(true)
    try {
      setCooldown(await sendCode(phone))
      setNotice({ tone: 'success', message: t('login.codeSent') })
      setFieldError(null)
    } catch (error) {
      applyError(error)
    } finally {
      setBusy(false)
    }
  }

  async function onSubmit(): Promise<void> {
    setBusy(true)
    try {
      // ⚠ 契约 A3：smsCode 与 password **二选一**，都给或都不给都会被服务端拒
      await login(method === 'sms' ? { phone, smsCode } : { phone, password })
    } catch (error) {
      applyError(error)
    } finally {
      setBusy(false)
    }
  }

  const canSubmit =
    phone.length > 0 && (method === 'sms' ? smsCode.length > 0 : password.length >= 8)

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 sm:items-center">
      <div className="w-full max-w-md rounded-t-2xl bg-white p-5 pb-[calc(1.25rem+var(--spacing-safe-bottom))] sm:rounded-2xl sm:pb-5">
        <h2 className="text-lg font-semibold">{t('login.title')}</h2>
        <p className="mt-1 text-xs text-neutral-500">{t('login.subtitle')}</p>

        {/*
          让用户看见自己没有白点。
          ⚠ 文案由挂起动作自己指定(`hintKey`)：发送拦截那个入口登录后**不会**自动继续，
             用默认的「登录后将继续：…」等于在界面上承诺一件我们不做的事(FE-5)。
        */}
        {pending ? (
          <div className="mt-3">
            <Notice tone="info">
              {t(pending.hintKey ?? 'login.pendingHint', { label: pending.label })}
            </Notice>
          </div>
        ) : null}

        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            void onSubmit()
          }}
        >
          <TextField
            label={t('login.phone')}
            placeholder={t('login.phonePlaceholder')}
            value={phone}
            onChange={setPhone}
            type="tel"
            inputMode="tel"
            maxLength={32}
            autoComplete="tel"
            error={fieldError?.field === 'phone' ? fieldError.message : undefined}
          />

          {method === 'sms' ? (
            <TextField
              label={t('login.smsCode')}
              placeholder={t('login.smsCodePlaceholder')}
              value={smsCode}
              onChange={setSmsCode}
              inputMode="numeric"
              maxLength={16}
              autoComplete="one-time-code"
              error={fieldError?.field === 'smsCode' ? fieldError.message : undefined}
              trailing={
                <Button
                  variant="ghost"
                  disabled={busy || cooldown > 0 || phone.length === 0}
                  onClick={() => void onSendCode()}
                >
                  {cooldown > 0 ? t('login.resendIn', { seconds: cooldown }) : t('login.sendCode')}
                </Button>
              }
            />
          ) : (
            <TextField
              label={t('login.password')}
              placeholder={t('login.passwordPlaceholder')}
              value={password}
              onChange={setPassword}
              type="password"
              maxLength={128}
              autoComplete="current-password"
              error={fieldError?.field === 'password' ? fieldError.message : undefined}
            />
          )}

          {notice ? (
            <Notice tone={notice.tone} traceId={notice.traceId}>
              {notice.message}
            </Notice>
          ) : null}

          <Button type="submit" block disabled={busy || !canSubmit}>
            {t('login.submit')}
          </Button>
        </form>

        <div className="mt-3 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setMethod(method === 'sms' ? 'password' : 'sms')
              setFieldError(null)
              setNotice(null)
            }}
          >
            {method === 'sms' ? t('login.byPassword') : t('login.bySms')}
          </Button>
          <Button variant="ghost" onClick={cancelLogin}>
            {t('login.close')}
          </Button>
        </div>
      </div>
    </div>
  )
}
